import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface FeatureFlag {
  key: string;
  enabled: boolean;
  description: string;
  category: 'ui' | 'feature' | 'integration' | 'experiment';
  createdAt: Date;
  updatedAt: Date;
}

export interface FeatureFlagConfig {
  [key: string]: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class FeatureFlagService {
  private readonly STORAGE_KEY = 'insee-feature-flags';
  
  private featureFlagsSubject = new BehaviorSubject<FeatureFlag[]>([]);
  private configSubject = new BehaviorSubject<FeatureFlagConfig>({});

  featureFlags$: Observable<FeatureFlag[]> = this.featureFlagsSubject.asObservable();
  config$: Observable<FeatureFlagConfig> = this.configSubject.asObservable();

  // Default feature flags for INSEE application
  private defaultFlags: FeatureFlag[] = [
    {
      key: 'enable_dark_mode',
      enabled: true,
      description: 'Enable dark mode theme toggle',
      category: 'ui',
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      key: 'enable_mobile_navigation',
      enabled: true,
      description: 'Enable mobile-optimized navigation',
      category: 'ui',
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      key: 'enable_offline_mode',
      enabled: true,
      description: 'Enable offline capabilities and data caching',
      category: 'feature',
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      key: 'enable_push_notifications',
      enabled: false,
      description: 'Enable push notifications for policy updates',
      category: 'feature',
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      key: 'enable_advanced_charts',
      enabled: true,
      description: 'Enable advanced chart components in dashboard',
      category: 'ui',
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      key: 'enable_bulk_operations',
      enabled: false,
      description: 'Enable bulk operations for policy management',
      category: 'feature',
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      key: 'enable_accessibility_tools',
      enabled: true,
      description: 'Enable enhanced accessibility tools and features',
      category: 'ui',
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      key: 'enable_real_time_updates',
      enabled: false,
      description: 'Enable real-time updates via WebSocket',
      category: 'integration',
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      key: 'enable_beta_features',
      enabled: false,
      description: 'Enable beta features for testing',
      category: 'experiment',
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      // IHUB redesign (Claude Design handoff) — vibrant red + gold-eagle.
      // Gates the replacing surfaces (home redesign) + the new bottom tab bar /
      // IA so the new look ships as a set and has an instant runtime kill-switch.
      // Flip to `false` for a staged prod rollout; can be overridden via the
      // FeatureFlagService (setFlag / localStorage) without a redeploy.
      key: 'enable_new_ui',
      enabled: true,
      description: 'Enable the redesigned IHUB customer UI (home, policies, bottom tab bar)',
      category: 'ui',
      createdAt: new Date(),
      updatedAt: new Date()
    }
  ];

  constructor() {
    this.loadFeatureFlags();
  }

  /**
   * Check if a feature is enabled
   */
  isEnabled(flagKey: string): boolean {
    const config = this.configSubject.value;
    return config[flagKey] || false;
  }

  /**
   * Enable a feature flag
   */
  enable(flagKey: string): void {
    this.updateFlag(flagKey, true);
  }

  /**
   * Disable a feature flag
   */
  disable(flagKey: string): void {
    this.updateFlag(flagKey, false);
  }

  /**
   * Toggle a feature flag
   */
  toggle(flagKey: string): void {
    const current = this.isEnabled(flagKey);
    this.updateFlag(flagKey, !current);
  }

  /**
   * Update multiple feature flags at once
   */
  updateFlags(updates: { [key: string]: boolean }): void {
    const currentFlags = this.featureFlagsSubject.value;
    const currentConfig = this.configSubject.value;
    
    const updatedFlags = currentFlags.map(flag => {
      if (updates.hasOwnProperty(flag.key)) {
        return {
          ...flag,
          enabled: updates[flag.key],
          updatedAt: new Date()
        };
      }
      return flag;
    });

    const updatedConfig = {
      ...currentConfig,
      ...updates
    };

    this.featureFlagsSubject.next(updatedFlags);
    this.configSubject.next(updatedConfig);
    this.saveFeatureFlags(updatedFlags);
  }

  /**
   * Get all feature flags
   */
  getAllFlags(): FeatureFlag[] {
    return this.featureFlagsSubject.value;
  }

  /**
   * Get flags by category
   */
  getFlagsByCategory(category: FeatureFlag['category']): FeatureFlag[] {
    return this.featureFlagsSubject.value.filter(flag => flag.category === category);
  }

  /**
   * Add a new feature flag
   */
  addFlag(flag: Omit<FeatureFlag, 'createdAt' | 'updatedAt'>): void {
    const newFlag: FeatureFlag = {
      ...flag,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    const currentFlags = this.featureFlagsSubject.value;
    const flagExists = currentFlags.some(f => f.key === flag.key);
    
    if (flagExists) {
      console.warn(`Feature flag '${flag.key}' already exists`);
      return;
    }

    const updatedFlags = [...currentFlags, newFlag];
    this.featureFlagsSubject.next(updatedFlags);
    
    const updatedConfig = {
      ...this.configSubject.value,
      [flag.key]: flag.enabled
    };
    this.configSubject.next(updatedConfig);
    
    this.saveFeatureFlags(updatedFlags);
  }

  /**
   * Remove a feature flag
   */
  removeFlag(flagKey: string): void {
    const currentFlags = this.featureFlagsSubject.value;
    const updatedFlags = currentFlags.filter(flag => flag.key !== flagKey);
    
    this.featureFlagsSubject.next(updatedFlags);
    
    const updatedConfig = { ...this.configSubject.value };
    delete updatedConfig[flagKey];
    this.configSubject.next(updatedConfig);
    
    this.saveFeatureFlags(updatedFlags);
  }

  /**
   * Reset all flags to default values
   */
  resetToDefaults(): void {
    this.featureFlagsSubject.next([...this.defaultFlags]);
    const defaultConfig = this.createConfigFromFlags(this.defaultFlags);
    this.configSubject.next(defaultConfig);
    this.saveFeatureFlags(this.defaultFlags);
  }

  /**
   * Load feature flags from remote config (placeholder for future implementation)
   */
  async loadRemoteConfig(): Promise<void> {
    // TODO: Implement remote feature flag loading
    // This would typically fetch from a feature flag service
  }

  private updateFlag(flagKey: string, enabled: boolean): void {
    const currentFlags = this.featureFlagsSubject.value;
    const flagIndex = currentFlags.findIndex(flag => flag.key === flagKey);
    
    if (flagIndex === -1) {
      console.warn(`Feature flag '${flagKey}' not found`);
      return;
    }

    const updatedFlags = [...currentFlags];
    updatedFlags[flagIndex] = {
      ...updatedFlags[flagIndex],
      enabled,
      updatedAt: new Date()
    };

    this.featureFlagsSubject.next(updatedFlags);
    
    const updatedConfig = {
      ...this.configSubject.value,
      [flagKey]: enabled
    };
    this.configSubject.next(updatedConfig);
    
    this.saveFeatureFlags(updatedFlags);
  }

  private loadFeatureFlags(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const storedFlags: FeatureFlag[] = JSON.parse(stored);
        // Merge defaults with stored toggles: start from defaults (so newly
        // added flags like `enable_new_ui` are always present even for returning
        // users), but keep each flag's stored enabled-state, plus any extra
        // stored flags not in defaults.
        const storedByKey = new Map(storedFlags.map(f => [f.key, f]));
        const merged: FeatureFlag[] = this.defaultFlags.map(d => storedByKey.get(d.key) ?? d);
        for (const f of storedFlags) {
          if (!this.defaultFlags.some(d => d.key === f.key)) { merged.push(f); }
        }
        this.featureFlagsSubject.next(merged);
        this.configSubject.next(this.createConfigFromFlags(merged));
      } else {
        // Use default flags
        this.resetToDefaults();
      }
    } catch (error) {
      console.error('Failed to load feature flags:', error);
      this.resetToDefaults();
    }
  }

  private saveFeatureFlags(flags: FeatureFlag[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(flags));
    } catch (error) {
      console.error('Failed to save feature flags:', error);
    }
  }

  private createConfigFromFlags(flags: FeatureFlag[]): FeatureFlagConfig {
    return flags.reduce((config, flag) => {
      config[flag.key] = flag.enabled;
      return config;
    }, {} as FeatureFlagConfig);
  }
}